<template>
  <div
    class="offcanvas offcanvas-fullscreen-sm-down offcanvas-end"
    tabindex="-1"
    id="offcanvaModifierForm"
    ref="offcanvaModifierForm"
    data-bs-backdrop="static"
  >
    <div
      class="offcanvas-header py-2 d-flex align-items-center gap-2"
      style="background: linear-gradient(135deg, #E96C15 0%, #d46010 100%); color: #fff;"
    >
      <i class="fa-solid fa-sliders" />
      <span class="fw-semibold">{{ $t('Product Modifier') }}</span>
      <button
        type="button"
        style="font-size: small;"
        class="btn-close btn-close-white shadow-none ms-auto"
        data-bs-dismiss="offcanvas"
        aria-label="Close"
      />
    </div>

    <div
      class="offcanvas-body"
      v-if="loading"
    >
      <placeholder-content :is-load="true" />
    </div>
    <div
      class="offcanvas-body"
      v-else-if="!hasPermission"
    >
      <access-denied/>
    </div>
    <div
      class="offcanvas-body p-0"
      v-else
    >
      <!-- Product Info Header -->
      <div class="border-bottom px-3 py-3" style="background: #fafafa;">
        <div class="fw-bold mb-2" style="font-size: 17px;">
          {{ data.name }}
        </div>
        <div class="d-flex flex-wrap align-items-center gap-2" style="font-size: 13px;">
          <span
            :class="['badge rounded-pill', data.active ? 'bg-success' : 'bg-danger']"
          >
            <i :class="['fa-solid me-1', data.active ? 'fa-circle-check' : 'fa-circle-xmark']" />
            {{ data.active ? $t('Active') : $t('Inactive') }}
          </span>
          <span
            :class="['badge rounded-pill', data.type == 'Configurale' ? 'bg-info text-dark' : 'bg-secondary text-white']"
          >
            <i class="fa-solid fa-tag me-1" />
            {{ $t(data.type) }}
          </span>
          <span class="text-muted" style="font-size: 12px;">
            <i class="fa-regular fa-clock me-1" />
            {{ formatDateTime(data.updated_at) }}
          </span>
        </div>
      </div>

      <!-- Simple Product (no config) -->
      <template v-if="!data.config">
        <div class="d-flex align-items-center px-3 pt-3 pb-2">
          <div style="width: 4px; height: 18px; background: #E96C15; border-radius: 2px;" class="me-2"></div>
          <span class="fw-semibold text-secondary small text-uppercase" style="letter-spacing: 0.04em;">
            <i class="fa-solid fa-layer-group me-1" />
            {{ $t('Modifier Groups') }}
          </span>
          <span class="badge bg-dark rounded-pill ms-2">{{ form.modifiers.length }}</span>
        </div>
        <div class="px-3 pb-2">
          <!-- Empty state -->
          <div
            v-if="form.modifiers.length === 0"
            class="text-center text-muted py-4 border rounded-3"
            style="background: #f8f9fa; border-style: dashed !important;"
          >
            <i class="fa-solid fa-puzzle-piece mb-2" style="font-size: 24px; opacity: 0.4;" />
            <div style="font-size: 13px;">{{ $t('Add Modifier') }}...</div>
          </div>

          <!-- Modifier Cards -->
          <draggable
            v-model="form.modifiers"
            item-key="id"
            handle=".drag-handle"
            :animation="200"
            ghost-class="drag-ghost"
            @end="syncModifierIds"
          >
            <template #item="{ element: modifier, index: mIndex }">
              <div
                class="card mb-2 border shadow-sm overflow-hidden"
              >
                <div
                  class="card-header d-flex align-items-center px-2 py-2"
                  :class="[!modifier.show_detail ? 'border-bottom-0' : '']"
                  style="font-size: 14px; background: #fff;"
                >
                  <span
                    class="drag-handle d-inline-flex align-items-center justify-content-center"
                    style="width: 22px; height: 22px; font-size: 12px; cursor: grab; touch-action: none; flex-shrink: 0;"
                    :title="$t('Drag to reorder')"
                  >
                    <i class="fa-solid fa-grip-vertical" />
                  </span>
                  <div
                    class="d-flex align-items-center gap-2 flex-wrap flex-grow-1 ms-1"
                    style="cursor: pointer; min-width: 0;"
                    @click="() => { modifier.show_detail = !modifier.show_detail }"
                  >
                    <i
                      :class="['fa-solid', modifier.show_detail ? 'fa-chevron-up' : 'fa-chevron-down']"
                      style="font-size: 11px; color: #E96C15; width: 14px; text-align: center;"
                    />
                    <span class="fw-semibold">{{ $ln(modifier) }}</span>
                    <span
                      v-if="$hasFeature('feature_inventory')"
                      :class="['badge rounded-pill', modifier.recipe_behavior === 'Stack' ? 'bg-primary' : 'bg-dark']"
                      style="font-size: 10px;"
                    >
                      <i :class="['fa-solid me-1', modifier.recipe_behavior === 'Stack' ? 'fa-cubes' : 'fa-chart-pie']" />
                      {{ $t(modifier.recipe_behavior) }}
                    </span>
                    <span class="badge rounded-pill bg-light text-dark border" style="font-size: 10px;">
                      {{ $t(modifier.select_type) }}
                    </span>
                    <span
                      v-if="modifier.required"
                      class="badge rounded-pill bg-warning text-dark"
                      style="font-size: 10px;"
                    >
                      {{ $t('Required') }}
                    </span>
                    <span
                      v-for="tag in modifier.tags"
                      :key="tag.name"
                      class="badge rounded-pill"
                      :style="'font-size: 10px; background: ' + tag.color + '; color: #fff;'"
                    >
                      <i class="fa-solid fa-tag me-1" />
                      {{ tag.name }}
                    </span>
                  </div>
                  <button
                    @click="deleteModifier(modifier.id)"
                    class="btn btn-sm btn-outline-danger border-0 px-2 py-1 ms-1"
                    style="font-size: 12px; flex-shrink: 0;"
                  >
                    <i class="fa-solid fa-trash-can" />
                  </button>
                </div>
                <div
                  class="card-body p-0"
                  v-if="modifier.show_detail"
                >
                  <div class="overflow-x-auto">
                    <table class="mb-0 table table-sm table-hover align-middle">
                      <thead style="font-size: 13px;">
                        <tr style="background: #f5f5f5;">
                          <th
                            class="text-end text-muted fw-medium"
                            style="min-width: 35px; max-width: 35px; width: 35px;"
                          >
                            #
                          </th>
                          <th
                            class="text-muted fw-medium"
                            style="min-width: 200px"
                          >
                            {{ $t('Option Name') }}
                          </th>
                          <th
                            class="text-end text-muted fw-medium"
                            style="min-width: 100px; max-width: 100px; width: 100px;"
                          >
                            {{ $t('Price') }}
                          </th>
                          <th
                            class="text-center text-muted fw-medium"
                            style="min-width: 90px; max-width: 90px; width: 90px;"
                          >
                            {{ $t('Default') }}
                          </th>
                        </tr>
                      </thead>
                      <tbody style="font-size: 13px;">
                        <tr
                          v-for="(option, index) in modifier.options"
                          :key="index"
                        >
                          <td class="text-end text-muted">
                            {{ index + 1 }}
                          </td>
                          <td>
                            <div class="fw-medium">{{ option.name }}</div>
                            <div
                              v-if="option.recipes.length > 0 && $hasFeature('feature_inventory')"
                              class="mt-1"
                              style="font-size: 12px;"
                            >
                              <i class="fa-solid fa-mortar-pestle me-1 text-muted" />
                              <span class="text-secondary">
                                {{ showRecipes(option.recipes) }}
                              </span>
                            </div>
                          </td>
                          <td class="text-end fw-medium">
                            {{ formatDigit(option.price) }}
                          </td>
                          <td class="text-center">
                            <div class="form-check d-flex justify-content-center m-0">
                              <input
                                v-if="modifier.select_type == 'Multiple Select'"
                                class="form-check-input shadow-none"
                                v-model="option.default"
                                type="checkbox"
                                :id="'defaultOption_' + mIndex + '_' + index"
                                disabled
                              >
                              <input
                                v-else
                                class="form-check-input shadow-none"
                                v-model="option.default"
                                type="radio"
                                :id="'defaultOption_' + mIndex + '_' + index"
                                :value="true"
                                disabled
                              >
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </template>
          </draggable>

          <!-- Add Modifier -->
          <div class="mt-2 mb-1">
            <div class="row">
              <div class="col-md-5">
                <div class="d-flex align-items-center gap-2">
                  <i class="fa-solid fa-plus text-muted" style="font-size: 12px;" />
                  <select2
                    v-model="selectModifier"
                    @select="onSelectedModifier"
                    :options="select2Modifiers.options"
                    :settings="select2Modifiers.setting"
                    :placeholder="$t('Add Modifier') + '...'"
                    style="flex: 1;"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Configurable Product (with variants) -->
      <template v-else>
        <!-- Variant Tabs -->
        <div class="border-bottom px-3 py-3" style="background: #fff;">
          <div class="d-flex align-items-center mb-2">
            <div style="width: 4px; height: 18px; background: #E96C15; border-radius: 2px;" class="me-2"></div>
            <span class="fw-semibold text-secondary small text-uppercase" style="letter-spacing: 0.04em;">
              <i class="fa-solid fa-code-branch me-1" />
              {{ $t('Variants') }}
            </span>
          </div>
          <div
            class="d-flex flex-nowrap gap-2 pb-1"
            style="overflow-x: auto; -webkit-overflow-scrolling: touch; scrollbar-width: thin;"
          >
            <button
              v-for="variant in select2Variants.options"
              :key="variant.id"
              type="button"
              class="btn btn-sm d-flex align-items-center gap-1 flex-shrink-0 shadow-none"
              :class="select2Variants.id == variant.id ? 'text-white' : 'btn-outline-secondary'"
              :style="select2Variants.id == variant.id
                ? 'background: #E96C15; border-color: #E96C15; font-size: 13px;'
                : 'font-size: 13px;'"
              @click="select2Variants.id = variant.id"
            >
              {{ variant.text }}
              <span
                v-if="form.variants.find(v => v.id == variant.id)?.modifiers.length"
                class="badge rounded-pill"
                :style="select2Variants.id == variant.id
                  ? 'background: rgba(255,255,255,0.25); font-size: 10px;'
                  : 'background: #e9ecef; color: #6c757d; font-size: 10px;'"
              >
                {{ form.variants.find(v => v.id == variant.id)?.modifiers.length }}
              </span>
            </button>
          </div>
        </div>

        <!-- Tab Content -->
        <template v-if="select2Variants.id">
          <div class="d-flex align-items-center px-3 pt-3 pb-2">
            <div style="width: 4px; height: 18px; background: #E96C15; border-radius: 2px;" class="me-2"></div>
            <span class="fw-semibold text-secondary small text-uppercase" style="letter-spacing: 0.04em;">
              <i class="fa-solid fa-layer-group me-1" />
              {{ $t('Modifier Groups') }}
            </span>
            <span class="badge bg-dark rounded-pill ms-2">
              {{ form.variants.find((variant) => variant.id == select2Variants.id)?.modifiers.length || 0 }}
            </span>
          </div>
          <div class="px-3 pb-2">
            <!-- Empty state -->
            <div
              v-if="form.variants.find((variant) => variant.id == select2Variants.id)?.modifiers.length === 0"
              class="text-center text-muted py-4 border rounded-3"
              style="background: #f8f9fa; border-style: dashed !important;"
            >
              <i class="fa-solid fa-puzzle-piece mb-2" style="font-size: 24px; opacity: 0.4;" />
              <div style="font-size: 13px;">{{ $t('Add Modifier') }}...</div>
            </div>

            <!-- Variant Modifier Cards -->
            <draggable
              v-model="currentVariantModifiers"
              item-key="id"
              handle=".drag-handle"
              :animation="200"
              ghost-class="drag-ghost"
              @end="syncVariantModifierIds"
            >
              <template #item="{ element: modifier, index: mIndex }">
                <div
                  class="card mb-2 border shadow-sm overflow-hidden"
                >
                  <div
                    class="card-header d-flex align-items-center px-2 py-2"
                    :class="[!modifier.show_detail ? 'border-bottom-0' : '']"
                    style="font-size: 14px; background: #fff;"
                  >
                    <span
                      class="drag-handle d-inline-flex align-items-center justify-content-center"
                      style="width: 22px; height: 22px; font-size: 12px; cursor: grab; touch-action: none; flex-shrink: 0;"
                      :title="$t('Drag to reorder')"
                    >
                      <i class="fa-solid fa-grip-vertical" />
                    </span>
                    <div
                      class="d-flex align-items-center gap-2 flex-wrap flex-grow-1 ms-1"
                      style="cursor: pointer; min-width: 0;"
                      @click="() => { modifier.show_detail = !modifier.show_detail }"
                    >
                      <i
                        :class="['fa-solid', modifier.show_detail ? 'fa-chevron-up' : 'fa-chevron-down']"
                        style="font-size: 11px; color: #E96C15; width: 14px; text-align: center;"
                      />
                      <span class="fw-semibold">{{ $ln(modifier) }}</span>
                      <span
                        v-if="$hasFeature('feature_inventory')"
                        :class="['badge rounded-pill', modifier.recipe_behavior === 'Stack' ? 'bg-primary' : 'bg-dark']"
                        style="font-size: 10px;"
                      >
                        <i :class="['fa-solid me-1', modifier.recipe_behavior === 'Stack' ? 'fa-cubes' : 'fa-chart-pie']" />
                        {{ $t(modifier.recipe_behavior) }}
                      </span>
                      <span class="badge rounded-pill bg-light text-dark border" style="font-size: 10px;">
                        {{ $t(modifier.select_type) }}
                      </span>
                      <span
                        v-if="modifier.required"
                        class="badge rounded-pill bg-warning text-dark"
                        style="font-size: 10px;"
                      >
                        {{ $t('Required') }}
                      </span>
                      <span
                        v-for="tag in modifier.tags"
                        :key="tag.name"
                        class="badge rounded-pill"
                        :style="'font-size: 10px; background: ' + tag.color + '; color: #fff;'"
                      >
                        <i class="fa-solid fa-tag me-1" />
                        {{ tag.name }}
                      </span>
                    </div>
                    <button
                      @click="deleteVariantModifier(modifier.id)"
                      class="btn btn-sm btn-outline-danger border-0 px-2 py-1 ms-1"
                      style="font-size: 12px; flex-shrink: 0;"
                    >
                      <i class="fa-solid fa-trash-can" />
                    </button>
                  </div>
                  <div
                    class="card-body p-0"
                    v-if="modifier.show_detail"
                  >
                    <div class="overflow-x-auto">
                      <table class="mb-0 table table-sm table-hover align-middle">
                        <thead style="font-size: 13px;">
                          <tr style="background: #f5f5f5;">
                            <th
                              class="text-end text-muted fw-medium"
                              style="min-width: 35px; max-width: 35px; width: 35px;"
                            >
                              #
                            </th>
                            <th
                              class="text-muted fw-medium"
                              style="min-width: 200px"
                            >
                              {{ $t('Option Name') }}
                            </th>
                            <th
                              class="text-end text-muted fw-medium"
                              style="min-width: 100px; max-width: 100px; width: 100px;"
                            >
                              {{ $t('Price') }}
                            </th>
                            <th
                              class="text-center text-muted fw-medium"
                              style="min-width: 90px; max-width: 90px; width: 90px;"
                            >
                              {{ $t('Default') }}
                            </th>
                          </tr>
                        </thead>
                        <tbody style="font-size: 13px;">
                          <tr
                            v-for="(option, index) in modifier.options"
                            :key="index"
                          >
                            <td class="text-end text-muted">
                              {{ index + 1 }}
                            </td>
                            <td>
                              <div class="fw-medium">{{ option.name }}</div>
                              <div
                                v-if="option.recipes.length > 0 && $hasFeature('feature_inventory')"
                                class="mt-1"
                                style="font-size: 12px;"
                              >
                                <i class="fa-solid fa-mortar-pestle me-1 text-muted" />
                                <span class="text-secondary">
                                  {{ showRecipes(option.recipes) }}
                                </span>
                              </div>
                            </td>
                            <td class="text-end fw-medium">
                              {{ formatDigit(option.price) }}
                            </td>
                            <td class="text-center">
                              <div class="form-check d-flex justify-content-center m-0">
                                <input
                                  v-if="modifier.select_type == 'Multiple Select'"
                                  class="form-check-input shadow-none"
                                  v-model="option.default"
                                  type="checkbox"
                                  :id="'vDefaultOption_' + mIndex + '_' + index"
                                  disabled
                                >
                                <input
                                  v-else
                                  class="form-check-input shadow-none"
                                  v-model="option.default"
                                  type="radio"
                                  :id="'vDefaultOption_' + mIndex + '_' + index"
                                  :value="true"
                                  disabled
                                >
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </template>
            </draggable>

            <!-- Add Variant Modifier -->
            <div class="mt-2 mb-1">
              <div class="row">
                <div class="col-md-5">
                  <div class="d-flex align-items-center gap-2">
                    <i class="fa-solid fa-plus text-muted" style="font-size: 12px;" />
                    <select2
                      v-model="selectVariantModifier"
                      @select="onSelectedVarinatModifier"
                      :options="select2Modifiers.options"
                      :settings="select2Modifiers.setting"
                      :placeholder="$t('Add Modifier') + '...'"
                      style="flex: 1;"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </template>
    </div>
    <div
      class="border-top d-flex justify-content-end align-items-center gap-2 px-3 py-2"
      style="background: #fafafa;"
      v-if="!loading && hasPermission"
    >
      <button
        type="button"
        data-bs-dismiss="offcanvas"
        class="btn btn-sm btn-light border"
      >
        {{ $t('Cancel') }}
      </button>
      <ladda
        text="Save"
        :btn-class="'btn btn-sm text-white'"
        :style="'background: #E96C15; border-color: #E96C15;'"
        :loading="loadingSave"
        :disabled="v$.$invalid"
        @click="handleUpdate"
      />
    </div>
  </div>
</template>

<script lang="js">
import AccessDenied from "@/plugins/AccessDenied.vue";
import { Products } from '@/services/endpoint/products.service';
import { AUTH, API_URLS } from '@/services/endpoint/_constants';
import signature  from '@/services/signature';
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import Ladda from '@/plugins/Ladda.vue';
import moment from "moment";
import numeral from 'numeral';
import Select2 from 'vue3-select2-component';
import draggable from 'vuedraggable';
import { useVuelidate } from '@vuelidate/core';
import validation from "./assets/template/validation-modifier";

export default {
  setup: () => ({ v$: useVuelidate() }),
  validations() {
    return validation(this.form);
  },
  props: {
    id: {
      type: Number,
      default: 0
    }
  },
  data() {
    let vm = this;
    return {
      loadingSave: false,
      hasPermission: false,
      loading: true,
      data: {},
      form: {
        is_config: '',
        modifier_ids: [],
        modifiers: [],
        variants: []
      },
      selectModifier: [],
      select2Modifiers: {
        options: [],
        setting: {
          dropdownParent: '#offcanvaModifierForm',
          minimumResultsForSearch: Infinity,
          multiple: true,
          language: {
            loadingMore: function (e) {
              return '<i class="fa-solid fa-spinner fa-spin" />'
            }
          },
          ajax: {
            beforeSend: function (request) {
              request.setRequestHeader('Authorization', 'Bearer ' + AUTH.TOKEN);
            },
            url: AUTH.URL + API_URLS.API_PRODUCT_MODIFIER,
            data: function (params) {
              var query = {
                search: params.term,
                page: params.page || 1,
                limit: 10
              };
              return signature($.param(query)).substring(1);
            },
            processResults: function (data, params) {
              let option = data.data;
              let pageCurrent = data.meta.current_page;
              option = option.map(function(item) {
                let disabled = false;
                if (!vm.form.is_config) {
                  disabled = vm.form.modifier_ids.find((id) => id == item.id)? true: false;
                } else {
                  const variant = vm.form.variants.find((variant) => variant.id == vm.select2Variants.id);
                  disabled = variant.modifier_ids.find((id) => id == item.id)? true: false;
                }
                return {
                  id: item.id,
                  text: item.name,
                  required: item.required,
                  select_type: item.select_type,
                  min_options: item.min_options,
                  max_options: item.max_options,
                  recipe_behavior: item.recipe_behavior,
                  options: item.options,
                  tags: item.tags || [],
                  disabled: disabled
                }
              });
              return {
                results: option,
                pagination: {
                  more: (pageCurrent * 10) < data.meta.total
                }
              }
            }
          },
          templateResult: this.templateResult
        }
      },
      selectVariantModifier: [],
      select2Variants: {
        options: [],
        setting: {
          dropdownParent: '#offcanvaModifierForm',
          placeholder: vm.$t('Select Variant') + '...',
          minimumResultsForSearch: true,
          multiple: false
        },
        id: ''
      }
    }
  },
  components: {
    AccessDenied,
    PlaceholderContent,
    Ladda,
    Select2,
    draggable
  },
  methods: {
    deleteModifier(id) {
      const indexModifierId = this.form.modifier_ids.indexOf(id);
      if (indexModifierId > -1) {
        this.form.modifier_ids.splice(indexModifierId, 1);
      }
      const indexModifier = this.form.modifiers.findIndex(modifier => modifier.id === id);
      if (indexModifier > -1) {
        this.form.modifiers.splice(indexModifier, 1);
      }
    },
    deleteVariantModifier(modifierId) {
      const variant = this.form.variants.find((variant) => variant.id == this.select2Variants.id);
      const indexModifierId = variant.modifier_ids.indexOf(modifierId);
      if (indexModifierId > -1) {
        variant.modifier_ids.splice(indexModifierId, 1);
      }
      const indexModifier = variant.modifiers.findIndex(modifier => modifier.id === modifierId);
      if (indexModifier > -1) {
        variant.modifiers.splice(indexModifier, 1);
      }
    },
    syncModifierIds() {
      this.form.modifier_ids = this.form.modifiers.map(m => m.id);
    },
    syncVariantModifierIds() {
      const variant = this.form.variants.find((v) => v.id == this.select2Variants.id);
      if (variant) {
        variant.modifier_ids = variant.modifiers.map(m => m.id);
      }
    },
    showRecipes(items) {
      return items.map(item => {
        return item.name + ' (' + this.formatNumber(item.quantity_display) + item.unit + ')';
      }).join(', ');
    },
    templateResult(option) {
      if (!option.id) return $('<div class="loading-small p-0" style="z-index:9999; height:0px;"></div>');
      let html = "<div class='d-flex justify-content-between mb-1' style='font-size: 18px;'>";
          html+= "  <div class='item-content'>";
          html+= "    " + option.text + "";
          if (option.recipe_behavior == 'Stack') {
            html+= "<span class='ms-2 badge bg-primary' style='font-size: 10px;'>";
            html+= "<i class='fa-solid fa-cubes me-1'></i>";
          } else {
            html+= "<span class='ms-2 badge bg-dark' style='font-size: 10px;'>";
            html+= "<i class='fa-solid fa-chart-pie me-1'></i>";
          }
          html+= this.$t(option.recipe_behavior);
          html+= " </span>";
          html+= "  </div>";
          html+= "</div>";
          html+= "<div class='item-bottom w-100' style='font-size: 12px;'>";
          html+= "  <span>";
          html+= "    <b>" + this.$t('Selection Type') + ': ' + "</b>";
          html+=      this.$t(option.select_type);
          html+= ",  </span>";
          html+= "  <span>";
          html+= "    <b>" + this.$t('Required Status') + ': ' + "</b>";
          if (option.required) {
            html+= "<span class='badge bg-success'>";
            html+= "<i class='fa-solid fa-check'></i>";
          } else {
            html+= "<span class='badge bg-secondary'>";
            html+= "<i class='fa-solid fa-xmark'></i>";
          }
          html+= option.required? this.$t('Yes'): this.$t('No');
          html+= " </span>";
          html+= "  </span>";
          html+= "</div>";
          if (option.tags && option.tags.length > 0) {
            html+= "<div class='mt-1'>";
            option.tags.forEach(function(tag) {
              html+= "<span class='badge rounded-pill me-1' style='font-size: 9px; background: " + tag.color + "; color: #fff;'>";
              html+= "<i class='fa-solid fa-tag me-1'></i>";
              html+= tag.name;
              html+= "</span>";
            });
            html+= "</div>";
          }
      return $(html);
    },
    async onSelectedModifier(e) {
      let vm = this;
      this.selectModifier = [];
      if (!e || !e.id) return;
      if (this.form.modifier_ids.find((id) => id == e.id)) return;
      this.form.modifiers.push({
        id: e.id,
        show_detail: false,
        name: e.text,
        required: e.required,
        select_type: e.select_type,
        min_options: e.min_options,
        max_options: e.max_options,
        recipe_behavior: e.recipe_behavior,
        options: e.options,
        tags: e.tags || [],
      });
      this.form.modifier_ids.push(e.id);
      this.select2Modifiers.options = [];
    },
    async onSelectedVarinatModifier(e) {
      let vm = this;
      this.selectVariantModifier = [];
      if (!e || !e.id) return;
      const variant = this.form.variants.find((variant) => variant.id == this.select2Variants.id);
      if (variant.modifier_ids.find((id) => id == e.id)) return;
      variant.modifiers.push({
        id: e.id,
        show_detail: false,
        name: e.text,
        required: e.required,
        select_type: e.select_type,
        min_options: e.min_options,
        max_options: e.max_options,
        recipe_behavior: e.recipe_behavior,
        options: e.options,
        tags: e.tags || [],
      });
      variant.modifier_ids.push(e.id);
      this.select2Modifiers.options = [];
    },
    formatDigit(value) {
      if ((value < 0.01) && value != 0) {
        return numeral(value).format("0,0.0000");
      }
      return numeral(value).format("0,0.00");
    },
    formatNumber(value) {
      return numeral(value).format("0,0");
    },
    async getInfoData() {
      this.loading = true;
      let vm = this;
      await Products.getUpdateModifier(this.id).then(res => {
        this.data = res.data.data;
        this.form.is_config = res.data.data.config;
        this.form.modifier_ids = res.data.data.modifier_ids;
        this.form.modifiers = res.data.data.modifiers.map(function(modifier) {
          return {
            id: modifier.id,
            max_options: modifier.max_options,
            min_options: modifier.min_options,
            name: modifier.name,
            recipe_behavior: modifier.recipe_behavior,
            required: modifier.required,
            select_type: modifier.select_type,
            show_detail: false,
            options: modifier.options,
            tags: modifier.tags || []
          }
        });
        this.form.variants = res.data.data.variants.map(function(variant) {
          return {
            id: variant.id,
            modifier_ids: variant.modifier_ids,
            modifiers: variant.modifiers.map(function(modifier) {
              return {
                id: modifier.id,
                max_options: modifier.max_options,
                min_options: modifier.min_options,
                name: modifier.name,
                recipe_behavior: modifier.recipe_behavior,
                required: modifier.required,
                select_type: modifier.select_type,
                show_detail: false,
                options: modifier.options,
                tags: modifier.tags || []
              }
            })
          }
        });
        this.select2Variants.options = res.data.data.variants.map(function(variant) {
          return {
            id: variant.id,
            text: variant.name
          }
        });
        if (this.select2Variants.options.length > 0) {
          this.select2Variants.id = this.select2Variants.options[0].id;
        }
        this.hasPermission = true;
        this.loading = false;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
        }
        this.loading = false;
			  throw err;
      });
    },
    setParamsBeforeSendUpdate() {
      let params = _.cloneDeep(this.form);
      if (params.is_config) {
        params = {
          is_config: this.form.is_config,
          variants: this.form.variants.map(function(variant) {
            return {
              id: variant.id,
              modifier_ids: variant.modifier_ids
            }
          })
        }
      } else {
        params = {
          is_config: this.form.is_config,
          modifier_ids: this.form.modifier_ids
        }
      }
      return params;
    },
    async handleUpdate() {
      this.loadingSave = true;
      this.v$.$touch();
      if(this.v$.$invalid) {
        this.loadingSave = false;
        return;
      }
      await Products.updateModifier(this.id, this.setParamsBeforeSendUpdate()).then(res => {
        this.$parent.fetchData();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Update Success!'));
        $(this.$refs.offcanvaModifierForm).offcanvas('hide');
        this.loadingSave = false;
      }).catch(err => {
        if (err?.response?.data?.errors) {
          this.errors = err?.response?.data?.errors;
        }
        this.loadingSave = false;
      });
    },
    formatDate(value) {
      return moment(value).format('DD/MM/YYYY');
    },
    formatDateTime(value) {
      return moment(value).format('YYYY-MM-DD HH:mm:ss');
    }
  },
  async mounted() {
    $(this.$refs.offcanvaModifierForm).offcanvas('show');

    $(this.$refs.offcanvaModifierForm).on('shown.bs.offcanvas',async () => {
      try {
        await this.getInfoData();
      } catch (err) {
      }
    });

    $(this.$refs.offcanvaModifierForm).on('hidden.bs.offcanvas', () => {
      this.$emit('close');
    });
  },
  computed: {
    currentVariantModifiers: {
      get() {
        const variant = this.form.variants.find((v) => v.id == this.select2Variants.id);
        return variant ? variant.modifiers : [];
      },
      set(value) {
        const variant = this.form.variants.find((v) => v.id == this.select2Variants.id);
        if (variant) variant.modifiers = value;
      }
    },
    costEstimate() {
      this.form.cost_estimate = this.form.recipes.reduce((sum, value) => {
        return sum + (value.total_cost*1);
      }, 0);

      return this.form.cost_estimate;
    }
  },
  watch: {
  }
}
</script>

<style scoped>
.drag-handle {
  color: #c4c9cf;
  transition: color 0.15s ease;
  user-select: none;
}
.drag-handle:hover {
  color: #E96C15;
}
.drag-handle:active {
  cursor: grabbing !important;
}
.drag-ghost {
  opacity: 0.5;
}
.sortable-chosen .drag-handle {
  color: #E96C15;
}
</style>
